import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { fetchJsonClient } from '#/api/client'
import { createEnumGuard } from '#/utils/typeGuards'
import type { TLocale } from '#lib/i18n'
import { SUPPORTED_LOCALES } from '#lib/i18n'

const isLocale = createEnumGuard(SUPPORTED_LOCALES)

/** Locales the backend API accepts for persistence */
type TApiLocale = 'en' | 'nl'
const API_LOCALES: TApiLocale[] = ['en', 'nl']
const isApiLocale = createEnumGuard<TApiLocale>(API_LOCALES)

interface ILanguageToggle {
  currentLocale: TLocale
  changeLanguage: (locale: TLocale) => void
}

export function useLanguageToggle(): ILanguageToggle {
  const { i18n } = useTranslation()

  const currentLocale: TLocale = isLocale(i18n.language) ? i18n.language : 'en'

  const changeLanguage = useCallback(
    (locale: TLocale): void => {
      i18n.changeLanguage(locale)

      // Persist locale preference to backend (fire-and-forget).
      // Only persist locales the API supports (en, nl).
      // Silently ignore rejection — localStorage fallback already handled by i18next.
      if (isApiLocale(locale)) void fetchJsonClient.PATCH('/auth/me/locale', { body: { locale } }).catch(() => {})
    },
    [i18n],
  )

  return { currentLocale, changeLanguage }
}
